import { useState, useEffect } from "react";
import AOS from 'aos';
import 'aos/dist/aos.css';
import './ViewCart.css';
import './Wishlist.css';
import { useSelector, useDispatch } from "react-redux";
import { removeProductFromCart, updateCartItemQuantity, clearUserCart } from "../redux/cartSlice";
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from "react-router-dom";
import { MdRemoveCircle } from "react-icons/md";
import { IoIosAddCircle } from "react-icons/io";
import { FaShoppingCart, FaShoppingBag } from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";
import ProductModal from '../components/ProductModal';
import styles from '../components/Card.module.css';
import { getProductById } from '../api';

const ViewCart = () => {
    // initialize AOS for animations
    const cart = useSelector(state => state.cart);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);

    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
    }, []);

    // Handle quantity change for cart items
    const handleQuantityChange = (product, newQuantity) => {
        // Find the current item in the cart matching the product and its selected options
        const currentItem = cart.items.find(item => item.id === product.id && item.selectedColor === product.selectedColor && item.selectedSize === product.selectedSize);
        if (!currentItem) return; 
        // If new quantity is less than 1, remove the item from cart
        if (newQuantity < 1) {
            dispatch(removeProductFromCart(currentItem)); 
        } else {
            dispatch(updateCartItemQuantity(currentItem, newQuantity));
        }
    };
    // Manage toast visibility state
    const [isToastVisible, setIsToastVisible] = useState(false);
    // Handle item removal with confirmation
    const handleRemoveItem = (item) => {
        setItemToRemove(item);
        setShowConfirm(true);
    };
    // Confirm item removal
    const handleConfirmRemove = async () => {
        if (itemToRemove) {
            // Dispatch action to remove item from cart
            try {
                // Remove item from cart
                await dispatch(removeProductFromCart(itemToRemove));
            } catch (err) {
                console.error(err);
                toast.error('Failed to remove item.');
            }
        }
        // Close confirmation modal and reset item to remove
        setShowConfirm(false);
        setItemToRemove(null);
    };
    // Handle clear cart with confirmation
    const handleClearCartClick = () => {
        setShowClearConfirm(true);
    };
    // handle confirm clear cart 
    const handleConfirmClearCart = async () => {
        if (isToastVisible) return;

        setShowClearConfirm(false);
        setIsToastVisible(true);
        try {
            // Clear the entire cart
            await dispatch(clearUserCart());
        } catch (err) {
            console.error(err);
            toast.error('Failed to clear cart.', { position: 'top-center', duration: 3000 });
        } finally {
            setIsToastVisible(false);
        }
    };
    // Group cart items by product ID
    const cartItems = cart?.items || [];
    // Group items by product ID to handle variants
    const groupedCartItems = cartItems.reduce((acc, item) => {
        const productId = item.id;
        // Initialize group if not exist
        if (!acc[productId]) {
            acc[productId] = {
                product: item,
                variants: []
            };
        }
        // Add item to the respective product group
        acc[productId].variants.push(item);
        return acc;
    }, {});

    // Helper function to safely format price
    const formatPrice = (price) => {
        // Convert price to number and format
        const numPrice = Number(price);
        return isNaN(numPrice) ? '₹0.00' : `₹${numPrice.toFixed(2)}`;
    };

    // Helper function to safely calculate subtotal
    const calculateSubtotal = (price, quantity) => {
        // calculate subtotal
        const numPrice = Number(price);
        const numQuantity = Number(quantity);
        return (isNaN(numPrice) || isNaN(numQuantity)) ? 0 : numPrice * numQuantity;
    };

    // Use totals directly from Redux state
    const displayTotal = cart.total || 0;
    const displayItemCount = cart.itemCount || 0;
    // If cart is empty, show empty cart message
    if (cartItems.length === 0) {
        return (
            <div className="wishlist-container">
                <div className="wishlist-header">
                    <div className="wishlist-title-section">
                        <div className="wishlist-icon-wrapper">
                            <FaShoppingCart className="wishlist-header-icon" />
                        </div>
                        <div>
                            <h1 className="wishlist-main-title">Shopping Cart</h1>
                            <p className="wishlist-subtitle">Your selected items</p>
                        </div>
                    </div>
                </div>
                
                {/* Empty page design */}
                <div className="wishlist-empty-state">
                    <div className="empty-state-icon-circle">
                        <FaShoppingCart className="empty-heart-icon" />
                    </div>
                    <h2 className="empty-state-title">Your Cart is Empty</h2>
                    <p className="empty-state-text">
                        Start adding items to your cart.
                        <br />They'll be saved here for you to checkout anytime!
                    </p>
                    <button 
                        className="btn-browse-products"
                        onClick={() => navigate('/products')}
                    >
                        <FaShoppingBag style={{ marginRight: '8px' }} />
                        Continue Shopping
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="container my-5" data-aos="fade-up">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4" data-aos="fade-right">
                        <h2 className="mb-0">Shopping Cart</h2>
                        <button className="btn btn-outline-danger scale-btn clear-cart-sticky" onClick={handleClearCartClick} data-aos="fade-left" >
                            Clear Cart
                        </button>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-8" data-aos="fade-up" data-aos-delay="100">
                    <div className="card" data-aos="zoom-in">
                        <div className="card-body">
                            {Object.values(groupedCartItems).map((group, groupIndex) => {
                                const isMultiVariant = group.variants.length > 1;
                                const firstVariant = group.variants[0];
                                
                                return (
                                <div key={`group-${firstVariant.id}-${groupIndex}`} style={{ marginBottom: '20px', borderBottom: '2px solid #f0f0f0', paddingBottom: '15px' }}>
                                    {/* Show product name once if multiple variants */}
                                    {isMultiVariant && (
                                        <div style={{ 
                                            backgroundColor: '#f8f9fa', 
                                            padding: '10px 15px', 
                                            borderRadius: '8px',
                                            marginBottom: '10px',
                                            border: '1px solid #e0e0e0'
                                        }}>
                                            <h6 style={{ margin: 0, color: '#82278A', fontWeight: '600', cursor: 'pointer' }} onClick={async () => {
                                                try {
                                                    const fullProduct = await getProductById(firstVariant.id);
                                                    setSelectedProduct(fullProduct.data);
                                                    setModalOpen(true);
                                                } catch (error) {
                                                    console.error('Failed to fetch product details:', error);
                                                }
                                            }}>
                                                📦 {firstVariant.title} 
                                                <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '10px' }}>
                                                    ({group.variants.length} variants)
                                                </span>
                                            </h6>
                                        </div>
                                    )}
                                    
                                    {/* Render each variant */}
                                    {group.variants.map((item, index) => (
                                <div key={item.cartItemId || `${item.id}-${item.variantId || index}`} className="row mb-4 border-bottom pb-3 cart-item-row" data-aos="fade-up" data-aos-delay={index * 200}>
                                    <div className="col-md-3 d-flex align-items-center">
                                        <img src={item.image} alt={item.title || 'Product Image'} className="cart-item-image" onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }} />
                                    </div>
                                    <div className="col-md-5 d-flex flex-column justify-content-center">
                                        <h5 className="card-title mb-2" style={{ cursor: "pointer", fontWeight: '600', color: '#333' }} onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }}>{item.title || 'Product'}</h5>
                                        
                                        {/* Variant Details */}
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', margin: '8px 0', fontSize: '0.95rem' }}>
                                            {item.selectedColor && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <strong style={{ color: "#82287A", minWidth: 'auto' }}>Color:</strong>
                                                    <span style={{
                                                        color: "#333",
                                                        backgroundColor: '#f0f0f0',
                                                        padding: '4px 10px',
                                                        borderRadius: '16px',
                                                        fontSize: '0.9rem',
                                                        fontWeight: '500'
                                                    }}>
                                                        {item.selectedColor}
                                                    </span>
                                                </div>
                                            )}
                                            {item.selectedSize && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <strong style={{ color: "#82287A", minWidth: 'auto' }}>Size:</strong>
                                                    <span style={{
                                                        color: "#333",
                                                        backgroundColor: '#f0f0f0',
                                                        padding: '4px 10px',
                                                        borderRadius: '16px',
                                                        fontSize: '0.9rem',
                                                        fontWeight: '500'
                                                    }}>
                                                        {item.selectedSize}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <p className="card-text text-muted" style={{ fontSize: "0.9rem", cursor: "pointer", marginTop: '8px' }} onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }}>
                                            {/* Description can be added here */}
                                        </p>
                                    </div>
                                    <div className="col-md-4 d-flex flex-column justify-content-center">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="h5 mb-0 text-brand">{formatPrice(item.price)}</span>
                                            <button className="btn btn-sm btn-outline-danger scale-btn" onClick={() => handleRemoveItem(item)}>
                                                Remove
                                            </button>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="me-2" style={{ marginBottom: "20px" }}>Quantity:</span>
                                            <div className={styles.quantityContainer}>
                                                <button className={styles.quantityButton} onClick={() => handleQuantityChange(item, (item.quantity || 1) - 1)} style={{ marginBottom: "20px" }}>
                                                    <MdRemoveCircle />
                                                </button>
                                                <span className={styles.quantity} style={{ marginBottom: "20px" }}>{item.quantity || 1}</span>
                                                <button className={styles.quantityButton} onClick={() => handleQuantityChange(item, (item.quantity || 0) + 1)} style={{ marginBottom: "20px" }}>
                                                    <IoIosAddCircle />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <strong className="text-brand">Subtotal: {formatPrice(calculateSubtotal(item.price, item.quantity))}</strong>
                                        </div>
                                    </div>
                                </div>
                            ))}
                                </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="col-lg-4 order-summary-sticky" data-aos="fade-up" data-aos-delay="200">
                    <div className="card" data-aos="zoom-in" data-aos-delay="300">
                        <div className="card-body">
                            <h5 className="card-title">Order Summary</h5>
                            <div className="d-flex justify-content-between mb-2">
                                
                                <span>Items ({displayItemCount})</span>
                                <span>{formatPrice(displayTotal)}</span>
                                </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Shipping</span>
                                <span>₹0.00</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between mb-3">
                                <strong>Total</strong>
                                <strong>{formatPrice(displayTotal)}</strong>
                            </div>
                            <Link to="/checkout" className="btn btn-primary w-100 mb-2 scale-btn" style={{ backgroundColor: "#82278A", borderColor: "#82278A" }} data-aos="zoom-in" data-aos-delay="400">
                                Proceed to Checkout
                            </Link>
                            <Link to="/products" className="btn btn-outline-secondary w-100 scale-btn" data-aos="zoom-in" data-aos-delay="500">
                                Continue Shopping
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            {/* Product Modal */}
            <ProductModal post={selectedProduct} isOpen={modalOpen} onClose={() => setModalOpen(false)} initialSelectedColor={selectedProduct?.selectedColor} initialSelectedSize={selectedProduct?.selectedSize} isWishlistPage={false} />

            {/* Remove Item Confirmation Modal */}
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: '2px solid #82278A' }}>
                    <Modal.Title style={{ color: '#82278A' }}>Confirm Removal</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ textAlign: 'center', padding: '30px', fontSize: '16px' }}>
                    Are you sure you want to remove this item from your cart?
                    {itemToRemove && (
                        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <strong>{itemToRemove.title}</strong>
                            {itemToRemove.selectedColor && itemToRemove.selectedSize && (
                                <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                                    {itemToRemove.selectedColor} / {itemToRemove.selectedSize}
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="confirm-actions" style={{ borderTop: 'none' }}>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)} className="confirm-btn cancel-btn">
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmRemove} className="confirm-btn" style={{ backgroundColor: '#82278A', borderColor: '#82278A'}}>
                        Remove
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Clear Cart Confirmation Modal */}
            <Modal show={showClearConfirm} onHide={() => setShowClearConfirm(false)} centered>
                <Modal.Header closeButton style={{ borderBottom: '2px solid #dc3545' }}>
                    <Modal.Title style={{ color: '#dc3545' }}>Clear Entire Cart?</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ textAlign: 'center', padding: '30px', fontSize: '16px' }}>
                    <p style={{ marginBottom: '15px' }}>Are you sure you want to clear all items from your cart?</p>
                    <p style={{ fontSize: '14px', color: '#666' }}>This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer className="confirm-actions" style={{ borderTop: 'none' }}>
                    <Button variant="secondary" onClick={() => setShowClearConfirm(false)} className="confirm-btn cancel-btn">
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmClearCart} className="confirm-btn">
                        Clear Cart
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ViewCart;